import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { addUser } from '../../services/UserService';
import { reportApiErrorGlobally } from '../../services/axiosConfig';
import { GUEST_UPGRADE_TOKEN_KEY, mergeGuest } from '../../services/ConnectService';
import { Mixpanel } from '../../services/Mixpanel';
import { trackGAEvent } from '../../services/GoogleAnalytics';
import { trackMetaEvent, trackMetaCustomEvent } from '../../services/MetaPixel';
import { trackChatGPTEvent, trackChatGPTCustomEvent } from '../../services/ChatGPTPixel';
import { primeMixpanelTaggedFlags } from '../../services/FeatureFlagService';
import { getCurrentUser } from '../../services/UserService';
import { DiffitUserContext } from '../../contexts/DiffitUserContext';
import { applyViewAsTeacher, resolveViewAsTeacher } from '../../utils/viewAsTeacher';
import { useCustomerio } from '../../contexts/CustomerIOContext';
import { ErrorProvider } from '../../contexts/ErrorContext';
import ErrorModal from '../../components/modals/ErrorModal';
import ProjectQuotaModal from '../../components/modals/ProjectQuotaModal';
import SignInSuccessModal from '../../components/modals/SignInSuccessModal';
import { SignInModalProvider, useSignInModal } from '../../contexts/SignInModalContext';
import { PremiumOfferProvider } from '../../contexts/PremiumOfferProvider';
import { PremiumOfferDeepLink } from '../../contexts/PremiumOfferDeepLink';
import { ReferralWelcomePrompt } from './ReferralWelcomePrompt';
import { useAuth, useUser } from '@clerk/react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { isDiffitBot, isPremiumDiffitBot } from '../../utils/users';
import { isAuthenticationFailure } from '../../utils/isAuthenticationFailure';
import { SIGN_OUT_TRIGGERS } from '../../utils/signOutTriggers';
import { signInSourceForPath } from '../../utils/signInCopy';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import {
  hasActiveSubscription,
  isFreeTrialUser,
  userHasCompletedTrialExtensionSurvey,
  daysLeftInTrial,
  getSubscriptionStatus,
} from '../../utils/isPremiumUser';
import { getOrSetDiffitSessionId } from '../../utils/diffitSessionId';
import {
  captureReferralFromUrl,
  clearReferralAttribution,
  readReferralAttribution,
} from '../../utils/referralAttribution';
import {
  captureEnrolmentFromUrl,
  clearEnrolmentSurface,
  readEnrolmentSurface,
} from '../../utils/referralEnrolment';
import { enrollInReferralProgram } from '../../services/ReferralService';
import { getUserLoginType } from '../../utils/users';
import { EmergencyFrontendBanner } from '../../components/EmergencyFrontendBanner';
import CustomerIOPlaceholder from '../../components/CustomerIOPlaceholder';
import { OnboardingWizardBTS2026 } from '../../components/modals/OnboardingWizardBTS2026';
import RefereeRewardModal, {
  recheckRefereeRewards,
} from '../../components/modals/RefereeRewardModal';
import { EditProfileModal } from '../../components/modals/EditProfileModal';
import BadgeAwardedModal from '../../components/modals/BadgeAwardedModal';
import { getBadgesToAnnounce } from '../../utils/badgeNotification';
import TutorialProvider from './Context/TutorialContext';
import { notifyExtensionOfSignIn } from '../../services/ExtensionBridge';
import SidebarVisibilityProvider from '../../contexts/SidebarVisibilityContext';
import { NAVBAR_MOBILE_HEIGHT } from '../../utils/layout';
import AppNavigation from './AppNavigation';
import { isFocusedFlow } from './focusedFlow';
import { getResolvedGeo, primeGeo } from '../../services/GeoService';

// User identity/subscription/profile values stamped onto every event as
// Mixpanel super-props (see registerUserPropsToMixpanel). Kept as a stable
// list so a signed-out load can atomically clear them — mixpanel.register
// merges, so without an explicit unregister a prior session's values would
// linger on subsequent anonymous events.
const USER_SUPER_PROP_KEYS = [
  'subscription_status',
  'is_premium_user',
  'is_free_trial_user',
  'is_active_individual_subscription',
  'is_in_premium_org',
  'is_in_premium_domain',
  'roles',
  'grade_levels',
  'subjects',
  'user_segments',
  'is_ambassador',
  'is_certified_educator',
  'is_star_teacher',
  'is_diffit_admin',
  'org_ids',
];

// Campaign pages register `landing_page` as a PERSISTED super-prop, and their
// unregister cleanup only runs on SPA unmount — a real anchor or
// window.location.reload skips it, which would stamp `landing_page` on every
// later event in that browser. So drop any stale one here, at module scope:
// App imports this module before any route's, which puts the clear strictly
// before the layout effect that registers a slug. In an effect it couldn't be:
// child layout effects flush before the parent's, so `/`'s HomeRoute would
// register its slug and this would wipe it in the same commit.
Mixpanel.replaceSuperProps({ unregister: ['landing_page'] });

// Kicked off at module scope, but kept as a promise so the identify effect can
// await the in-flight lookup instead of racing it. The anonymous contextKey is
// the literal string 'anon', so that effect identifies exactly once per tab —
// if geo lost the race, a brand-new visitor would carry no region for the whole
// session and could never match a rule. primeGeo never rejects and is bounded by
// its own timeout, so awaiting it cannot strand the effect.
const geoReady = primeGeo();

// ?signup= opens the sign-in modal on whatever route the visitor is on, packet and
// project routes included, so it takes its source from the path. Its own component
// because Base hosts the provider and so cannot consume it.
const ForcedSignupPrompt = () => {
  const [query, setQuery] = useSearchParams();
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  const showSignInModal = useSignInModal();

  useEffect(() => {
    if (query.get('signup') && !isSignedIn && isLoaded) {
      showSignInModal({ source: signInSourceForPath(location.pathname) });
      setQuery((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('signup');
        return next;
      });
    }
  }, [query, isSignedIn, isLoaded, setQuery, showSignInModal, location.pathname]);

  return null;
};

export default function Base() {
  const ldClient = useLDClient();
  const flags = useFlags();
  const location = useLocation();
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showOnboardingWizardBts2026, setShowOnboardingWizardBts2026] = useState(false);
  // separate surface from first-run onboarding: all questions at once, one Save.
  // Holds `true`, or a section key to open scrolled to (e.g. 'memories' from a chat chip).
  // Holds `true`, or a section key to open scrolled to (e.g. 'memories' from a chat chip).
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  // Belt-and-braces guard against the badge modal reappearing after it's been
  // acknowledged. The acknowledgement itself is server-side (badge_seen_<type>
  // in seen_nux_json), so the refresh normally empties badgesToAnnounce on its
  // own — this covers the window before that refresh lands, and the case where
  // the flags saved but the refresh failed.
  const [dismissedBadgeModal, setDismissedBadgeModal] = useState(false);
  const [showPendingNotification] = useState(false);
  // isLDidentified = "LaunchDarkly identify() has resolved" (success OR
  // failure) — gates login tracking so an ad-blocked LD doesn't permanently
  // drop the 'Successful Login' event. Also kept on DiffitUserContext
  // because route-level UI (packet, project, pricing) waits on it.
  // isLDIdentifySuccess = "identify resolved SUCCESSFULLY for the current
  // context" — gates Mixpanel flag-super-prop registration so we don't
  // stamp the previous context's flag values when LD's identify rejects
  // (LD keeps the last successful context's flag cache on failure).
  const [isLDidentified, setIsLDidentified] = useState(false);
  const [isLDIdentifySuccess, setIsLDIdentifySuccess] = useState(false);
  const [taggedFlagNames, setTaggedFlagNames] = useState(null);
  // Login response is never rendered — it's only consumed by the
  // login-tracking effect, which is already triggered when isLDidentified
  // transitions to true. Using a ref avoids the extra render cycles that a
  // useState would create when the response is written and cleared.
  const pendingLoginResponseRef = useRef(null);
  const loginTrackedRef = useRef(false);
  // In-flight POST /user. Both the `?success=true` path and the 404 recovery in
  // the load effect can fire for a single login — the ref lets the second
  // caller await the first registration instead of issuing its own (the
  // server-side packet association is not safe to run twice concurrently).
  const registerInFlightRef = useRef(null);
  const referralDrainInFlightRef = useRef(false);
  const enrolInFlightRef = useRef(false);
  // Tracks the LD context key (user.id or 'anon') we most recently identified
  // against. Lets the LD-identify effect skip work — and avoid resetting
  // isLDidentified, which gates route-level loading UI — when the effect
  // re-runs due to an unrelated state change (diffitUser refresh, org swap).
  const lastIdentifiedContextKeyRef = useRef(null);
  // De-dupe the client-side 'Flag State Identified' event so subsequent flag
  // changes or re-identifies don't re-emit it every time super-props update.
  const flagStateEmittedRef = useRef(false);
  // Deliberately NOT cleared on a context transition, unlike the ref above.
  // Marks which emission is the page load's first, which is what separates a
  // first-impression funnel entry from the re-emits that follow a mid-session
  // context change. See the `is_first_context` note on the emitting effect.
  const flagStateEverEmittedRef = useRef(false);

  const { user } = useUser();
  const { signOut: clerkSignOut, getToken, isSignedIn, isLoaded } = useAuth();
  const extensionNotifiedRef = useRef(false);
  // `trigger` records WHY the session ended (see SIGN_OUT_TRIGGERS for the
  // values). It is analytics-only — no sign-out, deliberate or forced, rotates
  // the user's Mixpanel identity any more. Rotating it mints a phantom
  // anonymous user against one unchanged diffitSessionId, inflating anonymous
  // counts and deflating anonymous-to-signup conversion. The case that argued
  // for rotating on a deliberate sign-out — the next person at this machine is
  // genuinely someone else — isn't common enough here to be worth a second
  // code path, so the same human keeps the same identity either way.
  const signOut = async (trigger = SIGN_OUT_TRIGGERS.USER_MENU) => {
    // Order matters: track 'Sign Out' first (captured with user identity +
    // super-props), clear the user super-props second, clerkSignOut last (may
    // navigate / invalidate the token used by Mixpanel's XHR). Await each step
    // so a future refactor that makes any of them take more async hops can't
    // reintroduce a race where a later step's side-effect lands on the prior
    // session.
    try {
      await Mixpanel.track('Sign Out', { trigger });
    } catch (err) {
      console.warn('Sign Out event failed to send', err);
    }
    // Mixpanel.reset() used to run here, and clearing Mixpanel's PERSISTED
    // super-props was a side effect of it. They live in the Mixpanel cookie and
    // survive the navigation below, and registerUserPropsToMixpanel can't clear
    // them on the next load until Clerk resolves isLoaded — so without this the
    // first events after a sign-out ('Page Viewed', anything firing before
    // Clerk resolves) would still carry the previous session's is_premium_user
    // / roles / org_ids. Clear them explicitly instead.
    await Mixpanel.replaceSuperProps({ unregister: USER_SUPER_PROP_KEYS });
    // reset() was also the only thing busting Mixpanel's memoized initPromise,
    // which is what re-fetches a fresh Clerk token for xhr_headers (see
    // Mixpanel.reset). Dropping it is only safe because sign-out navigates away
    // and discards module state. If sign-out ever becomes client-side-only,
    // Mixpanel's XHRs would keep the revoked bearer token baked in at the
    // original init, and this path would have to bust initPromise itself.
    await clerkSignOut();
    loginTrackedRef.current = false;
    flagStateEmittedRef.current = false;
    lastIdentifiedContextKeyRef.current = null;
    pendingLoginResponseRef.current = null;
    registerInFlightRef.current = null;
    setIsLDidentified(false);
    setIsLDIdentifySuccess(false);
    setDiffitUser(null);
  };

  const [query, setQuery] = useSearchParams();
  const [diffitUser, setDiffitUser] = useState();

  // Admins can drop their privileges for the tab (?viewAsTeacher=1) to design
  // and QA what a teacher sees. Masked here, once, so the profile menu, the
  // /admin guard, Eval and the referral gate cannot disagree about it.
  const isViewingAsTeacher = resolveViewAsTeacher(query);
  const viewedDiffitUser = useMemo(
    () => applyViewAsTeacher(diffitUser, isViewingAsTeacher),
    [diffitUser, isViewingAsTeacher]
  );

  // Capture referral attribution (?ref=<code> or ?i=<referral_id>) so it rides along
  // on the POST /user that creates the account, which can happen long after this load.
  // Also captures email-enrolment (?enroll=<surface>) for the drain effect below to send
  // once diffitUser exists. Depends on location.search (not []) so the /r/:code short link —
  // which lands here with no query string and only gets one after its own <Navigate> rewrite
  // to `/?ref=<code>` — is still captured; both capture functions are no-ops when their param
  // is absent, so re-running them on every search change is harmless. Deliberately
  // location.search, not setSearchParams: the setter's identity changes on every param
  // change, which would make this effect fire far more than intended.
  useEffect(() => {
    captureReferralFromUrl(location.search);
    captureEnrolmentFromUrl(location.search);
  }, [location.search]);

  /**
   * Tell the Diffit Chrome extension when sign-in completes, so its side panel
   * can reload itself — Clerk's syncHost doesn't propagate mid-session updates
   * to the extension reliably. No-op if the extension isn't installed.
   *
   * Gated on the Diffit account existing, not just on Clerk. On a brand-new
   * sign-up `isSignedIn` flips at setActive, before registerDiffitUser below has
   * written the account row — and this notification is what makes the extension
   * close the tab this page is running in. Firing on the Clerk transition
   * therefore killed the registration mid-flight: the teacher ended up with a
   * valid session and no account, GET /user 404ing, until their next visit here
   * recreated it. Measured in the extension's e2e — close the tab at Clerk time
   * and /user stays 404; leave the tab alive a few seconds and it returns 200.
   *
   * Declared below `diffitUser` on purpose: the dependency array is evaluated
   * during render, so reading it above the useState would be a TDZ error on
   * every render, not just an ordering nit.
   */
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn && diffitUser?.id) {
      if (!extensionNotifiedRef.current) {
        extensionNotifiedRef.current = true;
        notifyExtensionOfSignIn();
      }
    } else if (!isSignedIn) {
      // Reset only on a real sign-out. Clearing it whenever diffitUser is
      // momentarily undefined would let a single login notify more than once.
      extensionNotifiedRef.current = false;
    }
  }, [isLoaded, isSignedIn, diffitUser?.id]);
  const [selectedOrg, setSelectedOrg] = useState();
  const [finishedLoadingOnboardingState, setFinishedLoadingOnboardingState] = useState(false);
  const [isLoadingDiffitUser, setIsLoadingDiffitUser] = useState(false);
  const diffitSessionId = getOrSetDiffitSessionId();
  const { cioIdentify, cioPage } = useCustomerio();
  const isPremiumUser =
    hasActiveSubscription(diffitUser) || isFreeTrialUser(user, diffitUser) || isPremiumDiffitBot();
  const canHaveFreeTrialExtension = !userHasCompletedTrialExtensionSurvey(diffitUser);
  const { breakpoint } = useBreakpoints();
  const isMobile = ['xs', 'sm'].includes(breakpoint);
  // The onboarding wizard must never appear over the pricing / quote pages — a
  // prospective buyer deep-linking to a quote shouldn't be interrupted by NUX.
  const isOnPricingPage = location.pathname.startsWith('/pricing');

  // Badges are granted by an admin out of band, and nothing on the server tells
  // the teacher, so the announcement is derived here from the badge list the
  // /user response already carries. A badge is a nice moment but never an urgent
  // one, so it yields to first-run onboarding and to any focused flow.
  const badgesToAnnounce = useMemo(() => getBadgesToAnnounce(diffitUser), [diffitUser]);
  const showBadgeAwardedModal =
    badgesToAnnounce.length > 0 &&
    !dismissedBadgeModal &&
    !showOnboardingWizardBts2026 &&
    !isOnPricingPage &&
    !isFocusedFlow(location.pathname, location.search);

  const showNuxModal = (diffitUser) => {
    let seenNux = diffitUser.seen_nux_json ? diffitUser.seen_nux_json : {};
    // A brand-new user signing up mid-consent sees the wizard on their first
    // real visit to the app instead.
    const isOnFocusedPath = isFocusedFlow(window.location.pathname, window.location.search);

    if (isOnFocusedPath) {
      setShowOnboardingWizardBts2026(false);
      return;
    }

    // ?onboarding=true force-opens the wizard (testing / support). Strip it once
    // consumed: closing the wizard re-runs this function. Mutate `query` itself,
    // as the register path's later setQuery(query) would otherwise re-add it.
    if (new URLSearchParams(window.location.search).has('onboarding')) {
      query.delete('onboarding');
      setQuery(query, { replace: true });
      setShowOnboardingWizardBts2026(true);
      return;
    }

    const onboardingWizardValue = seenNux['has_completed_onboarding_bts_2026'];
    if (!onboardingWizardValue) {
      // they haven't completed the onboarding wizard yet
      setShowOnboardingWizardBts2026(true);
      return;
    }

    if (typeof onboardingWizardValue === 'string') {
      // they've dismissed the onboarding wizard; let's check it it's time to show it again
      const [type, timestamp] = onboardingWizardValue.split(':::');
      if (type === 'remind_later') {
        // check if timestamp is in the past
        if (timestamp < Date.now()) {
          setShowOnboardingWizardBts2026(true);
          return;
        }
      }
    }

    setFinishedLoadingOnboardingState(true);
  };

  // Returns the promise so callers can await the refresh; never rejects.
  const refreshDiffitUser = () => {
    return getCurrentUser()
      .then((response) => {
        setUpDiffitUser(response);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const setUpDiffitUser = (response) => {
    setDiffitUser(response.data);
    if (response.data) {
      cioIdentify({
        id: response.data.id,
        email: response.data.email,
      });
    }
    if (response.data.subjects) {
      Mixpanel.people.set({
        subjects: response.data.subjects.map((subject) => subject.subject),
      });
    }
    if (response.data.grade_levels) {
      Mixpanel.people.set({
        grade_levels: response.data.grade_levels.map((gradeLevel) => gradeLevel.grade_level),
      });
    }
    if (response.data.user_segments && response.data.user_segments.length > 0) {
      Mixpanel.people.set({
        user_segments: response.data.user_segments.map((segment) => segment.name),
      });
    }
    if (response.data.roles) {
      Mixpanel.people.set({
        roles: response.data.roles.map((r) => r.role),
      });
    }
    Mixpanel.people.set({
      is_premium_user: response.data.is_premium,
      is_free_trial_user: response.data.is_free_trial,
      is_active_individual_subscription: response.data.is_active_individual_subscription,
      is_in_premium_org: response.data.is_in_premium_org,
      is_in_premium_domain: response.data.is_in_premium_domain,
      is_ambassador: response.data.is_ambassador,
      is_certified_educator: response.data.is_certified_educator,
      is_star_teacher: response.data.is_star_teacher,
      is_diffit_admin: response.data.is_diffit_admin,
      user_login_type: getUserLoginType(user),
      org_ids: response.data.org_user_infos?.map((org) => org.org_id),
    });

    // Check local storage for selected org
    let storedOrg = JSON.parse(localStorage.getItem('selectedOrg'));
    // Check to make sure the ID is in the active_orgs array
    // Filter
    let previouslySelectedOrg = response.data.active_orgs.find(
      (org) => org.org_id === storedOrg?.org_id
    );
    if (storedOrg && !previouslySelectedOrg) {
      localStorage.removeItem('selectedOrg');
    }
    // Set selected org to an org you are an admin of, otherwise the stored org if it exists, otherwise set to first org in array
    let adminOrg = response.data.active_orgs.find((org) => org.role === 'Admin');
    setSelectedOrg(
      adminOrg
        ? adminOrg
        : previouslySelectedOrg
          ? previouslySelectedOrg
          : response.data.active_orgs[0]
    );
    // Save either stored org or first org in array to local storage, if it exists
    if (previouslySelectedOrg) {
      localStorage.setItem('selectedOrg', JSON.stringify(previouslySelectedOrg));
    } else if (response.data.active_orgs[0]) {
      localStorage.setItem('selectedOrg', JSON.stringify(response.data.active_orgs[0]));
    }

    showNuxModal(response.data);
  };

  // A user-load failure we deliberately don't sign out over leaves the teacher
  // signed in to Clerk with no Diffit profile, and the LD-identify effect below
  // waits for diffitUser before it resolves isLDidentified. Route-level UI
  // (packet, project, pricing) renders NOTHING until that flips, and the load
  // effect has no retry — so without this the transient failure trades a silent
  // sign-out for a permanently blank page, which is the worse of the two for the
  // teacher. Resolve the gate here and leave isLDIdentifySuccess false, exactly
  // as a rejected LD identify is handled: unblock the UI, don't claim flag
  // attribution we don't have. Flags stay on the anonymous bootstrap context —
  // the same values these routes rendered against before, when the failure
  // signed the user out.
  const resolveLDGateWithoutProfile = () => {
    setIsLDidentified(true);
  };

  useEffect(() => {
    // First check if the user has just logged in. The "success" query param will be set.
    const success = query.get('success');
    const emailId = query.get('email_id');
    const signout = query.get('signout');
    const share = query.get('share');
    if (share) {
      if (isLoaded) {
        query.delete('share');
        setQuery(query);
        return;
      }
    }
    if (emailId) {
      Mixpanel.track('Clicked Email Link', { emailId: emailId });
      query.delete('email_id');
      setQuery(query);
      return;
    }
    if (signout) {
      clerkSignOut();
      query.delete('signout');
      setQuery(query);
      return;
    }
    // The "invited" param is read by the public folder page (/c/:id) to show the
    // "you've been invited to join" banner to a logged-out invitee, so it must persist
    // across this load effect — don't strip it.
    // The packetId should be extracted only if the current_url is /packet/:packetId
    const current_url = window.location.pathname;
    const packetId =
      current_url.split('/')[1] === 'p' || current_url.split('/')[1] === 'packet'
        ? current_url.split('/')[2]
        : null;

    // POST /user — creates the Diffit account behind the current Clerk session
    // (this is the ONLY thing that creates one) and associates the packet the
    // visitor was viewing. Both callers below can fire for a single login, so
    // de-dupe: the ref holds the in-flight promise and later callers await
    // that instead of issuing a second registration.
    const registerDiffitUser = () => {
      if (registerInFlightRef.current) return registerInFlightRef.current;

      const inFlight = getToken({ skipCache: true })
        .then((token) => addUser(packetId, token))
        .then((response) => {
          // Identify Mixpanel immediately so that subsequent people.set
          // calls — both the ones inside setUpDiffitUser AND any fired
          // before the login-tracking effect runs — land on the user's
          // profile, not the anonymous $device profile.
          Mixpanel.identify(response.data.id);
          // Defer the 'Successful Login' track event and the user-fields
          // people.set to an effect that waits for LaunchDarkly to
          // identify the user, so the event carries accurate flag_*
          // super-properties. See the "login tracking" effect below.
          // Written to a ref so populating it doesn't kick off an
          // extra render — the subsequent setDiffitUser below triggers
          // the LD-identify effect, which will flip isLDidentified and
          // wake the login-tracking effect via its isLDidentified dep.
          pendingLoginResponseRef.current = { user: response.data, trackLoginEvent: true };
          setUpDiffitUser(response);
          // addUser spreads the stored capture into the body, so this call has drained it —
          // forget it, or the referral drain below re-sends the same code on the next load.
          clearReferralAttribution();
          // Gated on `success`: the 404 recovery runs without the param, and
          // calling setQuery with unchanged params would still push a junk
          // history entry (and drop any #fragment from the URL).
          if (!packetId && success) {
            // if the user is on the packet page, it will handle the success query param differently
            query.delete('success');
            setQuery(query);
          }
        })
        .catch((error) => {
          // A failed attempt must not poison later recoveries (the 404 branch
          // below retries on subsequent loads) — clear the cache before
          // re-throwing so the next caller issues a fresh registration.
          registerInFlightRef.current = null;
          throw error;
        });
      registerInFlightRef.current = inFlight;
      return inFlight;
    };

    // POST /user again for a teacher who was ALREADY signed in when they followed a
    // referral link. That endpoint attributes on every sign-in, not just at account
    // creation (see async_attribute_referral's callers), but the only client that ever
    // sent it the code was registerDiffitUser above — which this visitor never reaches,
    // so their capture used to sit in localStorage until it aged out. Everything POST
    // /user does is idempotent, so repeating it costs a refreshed last_sign_in_time and
    // nothing else.
    //
    // Deliberately NOT registerDiffitUser(): that one arms pendingLoginResponseRef with
    // trackLoginEvent, which would fire a 'Successful Login' on an ordinary page load.
    const drainReferralAttribution = (userId) => {
      if (referralDrainInFlightRef.current || !readReferralAttribution()) return;
      // This effect re-runs whenever the search params change, and the capture is still
      // present until the request comes back — without this guard a param change mid-flight
      // sends a second concurrent POST.
      referralDrainInFlightRef.current = true;
      getToken({ skipCache: true })
        // No packet_id: that argument exists so registration can claim a resource made
        // while logged out. This teacher already has an account, so passing it would
        // silently associate whatever packet they happen to be viewing.
        // The no-op handler keeps the failure off-screen — addUser's default message is
        // "There was an error logging you in", which the axios interceptor turns into a
        // blocking modal. On an ordinary page load, for someone already signed in and
        // working, that message is both wrong and unactionable.
        .then((token) => addUser(null, token, () => {}))
        .then(() => {
          // The attribution may have granted the referee a reward, and RefereeRewardModal
          // has already fetched-and-latched off the GET above — it looked before the grant
          // existed. Without this the teacher is told nothing until their next full page
          // load, which is the one thing that makes this feature read as broken.
          recheckRefereeRewards(userId);
          // Both, and neither alone is enough: clearing storage while `?ref=` is still in
          // the address bar lets the capture effect re-store it on the next load, and
          // stripping the param while the record stands leaves the drain firing anyway.
          clearReferralAttribution();
          if (query.has('ref') || query.has('ref_src') || query.has('i')) {
            query.delete('ref');
            query.delete('ref_src');
            query.delete('i');
            setQuery(query, { replace: true });
          }
        })
        // Cleared on a successful request, not a successful attribution: a code the
        // backend refuses comes back 200 just like one it accepts, and neither becomes
        // eligible by being retried. A failure leaves the record for the next load.
        .catch((error) => {
          // Never surfaced and never awaited by the bootstrap below: the user is already
          // loaded and usable, and a referral is not worth delaying or breaking that for
          // (the server side takes the same view — see the try/except around
          // async_attribute_referral in routers/user.py).
          console.log(error);
        })
        .finally(() => {
          referralDrainInFlightRef.current = false;
        });
    };

    if (success && isSignedIn && isLoaded && !isLoadingDiffitUser) {
      setIsLoadingDiffitUser(true);
      // If the user has just logged in, then let's POST them to the backend.
      registerDiffitUser()
        .catch((error) => {
          // Only a genuinely rejected session should end the session. A 5xx or
          // timeout from addUser — or a network blip from getToken — leaves
          // the teacher signed in to Clerk without a Diffit record loaded:
          // recoverable on the next load, and far better than silently logging
          // them out.
          if (isAuthenticationFailure(error)) {
            signOut(SIGN_OUT_TRIGGERS.ADD_USER_FAILURE);
          } else {
            resolveLDGateWithoutProfile();
          }
          console.log(error);
        })
        .finally(() => {
          setIsLoadingDiffitUser(false);
        });
      // If the user has not just logged in, then let's get their user info from the backend.
    } else if (isLoaded && isSignedIn && !isLoadingDiffitUser) {
      setIsLoadingDiffitUser(true);
      getCurrentUser((error) => {
        // 404 is an expected state here (see the catch below) — don't let the
        // global handler pop a "Something went wrong!" modal for it.
        if (error?.response?.status !== 404) {
          reportApiErrorGlobally(error);
        }
      })
        .then((response) => {
          Mixpanel.identify(response.data.id);
          // Don't clobber a pending fresh-login response from Path A. After
          // Path A removes the `success` param via setQuery, this load effect
          // re-runs and falls into Path B — isLoadingDiffitUser was already
          // reset to false by Path A's .finally, which fires before addUser
          // resolves (the outer .then doesn't return the addUser promise). If
          // getCurrentUser then wins the race against LD identify before the
          // login-tracking effect consumes the ref, overwriting it here with
          // trackLoginEvent:false would suppress the 'Successful Login' event.
          // (loginTrackedRef can't guard this — it's only set inside that
          // effect, which hasn't run yet.)
          if (!pendingLoginResponseRef.current) {
            pendingLoginResponseRef.current = { user: response.data, trackLoginEvent: false };
          }
          setUpDiffitUser(response);
          // Only this branch: the 404 path below and Path A both reach registerDiffitUser,
          // which already sends the capture. The user context is set from the GET above and
          // isn't refreshed from the POST — a referee reward the attribution grants arrives
          // by pinging RefereeRewardModal once the POST lands (see recheckRefereeRewards).
          drainReferralAttribution(response.data.id);
        })
        .catch((error) => {
          if (error?.response?.status === 404) {
            // Valid Clerk session, but no Diffit account exists yet. This is a
            // brand-new signup: SignInModal calls setActive() — flipping
            // isSignedIn — BEFORE ?success=true lands, so this branch can run
            // before Path A ever gets a chance; and if the sign-up redirect
            // was interrupted, Path A never runs at all. addUser is the ONLY
            // writer of the account row, so nothing else will ever create it —
            // create the account here instead of stranding the session.
            return registerDiffitUser().catch((registerError) => {
              if (isAuthenticationFailure(registerError)) {
                signOut(SIGN_OUT_TRIGGERS.ADD_USER_FAILURE);
              } else {
                resolveLDGateWithoutProfile();
              }
              console.log(registerError);
            });
          }
          // This branch runs on EVERY page load for every signed-in user, so it
          // is the highest-traffic sign-out path in the app. Any transient
          // GET /user failure used to sign a teacher out mid-session — nothing
          // in the UI clicked — and rotate their Mixpanel identity with it. The
          // request's own error handling (axios interceptor → ErrorModal) still
          // surfaces the failure; we just don't destroy the session over it.
          if (isAuthenticationFailure(error)) {
            signOut(SIGN_OUT_TRIGGERS.AUTH_FAILURE);
          } else {
            resolveLDGateWithoutProfile();
          }
          console.log(error);
        })
        .finally(() => {
          setIsLoadingDiffitUser(false);
        });
    } else if (isLoaded && !isSignedIn) {
      setDiffitUser(null);
    }
  }, [query, isSignedIn, isLoaded]);

  // MCP guest auto-link: the /connect consent page stores the guest's signed upgrade
  // token in localStorage when a teacher connects Claude as a guest. The first time
  // they sign in with a real account — minutes or days later, on any page — link the
  // guest's resources to it. merge_guest is idempotent server-side.
  const guestMergeAttemptedRef = useRef(false);
  useEffect(() => {
    if (!isLoaded || !isSignedIn || guestMergeAttemptedRef.current) return;
    const upgradeToken = localStorage.getItem(GUEST_UPGRADE_TOKEN_KEY);
    if (!upgradeToken) return;
    guestMergeAttemptedRef.current = true;
    mergeGuest(upgradeToken)
      .then(() => {
        localStorage.removeItem(GUEST_UPGRADE_TOKEN_KEY);
        Mixpanel.track('MCP Guest Auto-Linked');
      })
      .catch((error) => {
        // Invalid/expired token → drop it so we don't retry on every load;
        // transient failures keep the token and retry on the next page load.
        if (error?.response?.status === 400) {
          localStorage.removeItem(GUEST_UPGRADE_TOKEN_KEY);
        }
      });
  }, [isSignedIn, isLoaded]);

  // Send a captured email enrolment (?enroll=<surface>) once the Diffit account exists.
  // Gated on diffitUser, not isSignedIn: on a brand-new signup Clerk's setActive() flips
  // isSignedIn before the account row exists, the same gate the extension-notify effect
  // above documents and for the same reason.
  useEffect(() => {
    // diffitUser gets a new object identity on every refreshDiffitUser() (onboarding-wizard
    // close, profile edit), so this effect re-runs within one session — an in-flight ref,
    // not a one-shot latch, is what stops a request already underway from being duplicated.
    if (!diffitUser || enrolInFlightRef.current) return;
    const surface = readEnrolmentSurface();
    if (!surface) return;
    enrolInFlightRef.current = true;

    const stripEnrollParam = () => {
      // Both together: clearing storage while `?enroll=` is still in the address bar would
      // let the capture effect re-store it next load, and stripping while the record stands
      // would leave this effect firing anyway.
      clearEnrolmentSurface();
      if (query.has('enroll')) {
        setQuery(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('enroll');
            return next;
          },
          { replace: true }
        );
      }
    };

    enrollInReferralProgram(surface)
      .then(() => {
        // Cleared on a completed request, not on a successful enrolment: a surface the
        // backend can't place comes back 200 exactly like one it enrols, and retrying
        // doesn't make it placeable.
        Mixpanel.track('Referral Email Enrolled', { surface });
        stripEnrollParam();
      })
      .catch((error) => {
        Mixpanel.track('Referral Email Enrol Failed', { surface, error: error?.message });
        // A 404 is definitive, not retryable, so it clears the stash like a 200 does.
        // Anything else is a network error or a 5xx: leave the stash and the param in place
        // so the next load tries again.
        if (error?.response?.status === 404) stripEnrollParam();
      })
      .finally(() => {
        enrolInFlightRef.current = false;
      });
  }, [diffitUser, query, setQuery]);

  useEffect(() => {
    if (!isLoaded || !ldClient) return;
    if (isSignedIn && !diffitUser) return; // Wait for the user to get logged in

    // Serialize every attribute LD targets on into the context key. A
    // change to any of them (premium upgrade mid-session, org swap,
    // trial conversion, etc.) must re-identify so flag values track the
    // new attributes. A pure user.id check would leave flag state frozen
    // against the old attributes until a full page refresh. When NONE
    // of the targeting attributes change, the effect short-circuits —
    // prevents flicker of route-level loading UI that gates on
    // isLDidentified when diffitUser mutates for unrelated reasons.
    const allOrgIds = diffitUser ? diffitUser.org_user_infos?.map((org) => org.org_id) || [] : [];
    const userAttributes = diffitUser
      ? {
          isActiveIndividualSubscription: !!diffitUser.is_active_individual_subscription,
          isInPremiumOrg: !!diffitUser.is_in_premium_org,
          isFreeTrial: !!diffitUser.is_free_trial,
          isPremium: !!diffitUser.is_premium,
          isAmbassador: !!diffitUser.is_ambassador,
          isCertifiedEducator: !!diffitUser.is_certified_educator,
          isStarTeacher: !!diffitUser.is_star_teacher,
          createdAt: diffitUser.created_at,
          orgIds: allOrgIds.slice(0, 50),
          schoolRecordIds: (diffitUser.school_records?.map((school) => school.id) ?? []).sort(),
          selectedOrgId: selectedOrg?.org_id || null,
          // Profile attributes, so flags can target on subject / grade level
          // the same way they target on region. LD applies operators
          // element-wise to array attributes, so a "subjects contains Math"
          // rule matches without any flattening here.
          //
          // Sorted because contextKey below is a JSON.stringify of this
          // object: unsorted arrays would mint a new key whenever the API
          // returns the same values in a different order, triggering a
          // spurious identify() round trip on every mount.
          subjects: (diffitUser.subjects?.map((s) => s.subject) ?? []).sort(),
          gradeLevels: (diffitUser.grade_levels?.map((g) => g.grade_level) ?? []).sort(),
          // Active org's school, then the user's schools; spread after getResolvedGeo() so it
          // overrides the IP region, and omitted when unresolved so the IP region stands.
          ...(diffitUser.profile_region && { region: diffitUser.profile_region }),
        }
      : null;
    const contextKey = isSignedIn ? JSON.stringify({ id: user.id, ...userAttributes }) : 'anon';
    if (lastIdentifiedContextKeyRef.current === contextKey) return;
    lastIdentifiedContextKeyRef.current = contextKey;

    // Real context transition — block downstream effects until identify
    // resolves so they don't stamp events with the previous context's
    // flag values during the identify roundtrip. Also clear the once-per-
    // context Flag State Identified de-dupe so a mid-session context
    // change (org swap, premium upgrade) re-emits the event with the new
    // flag values; without this, only the very first identify per tab
    // would ever be logged.
    setIsLDidentified(false);
    setIsLDIdentifySuccess(false);
    flagStateEmittedRef.current = false;

    // mpDeviceId is the bucketing attribute for experiment flags — Mixpanel's
    // $device_id, so LD's bucketing unit and Mixpanel's counting unit are the
    // same value and there is exactly one Mixpanel identity per bucket. Read
    // asynchronously because it only exists once mixpanel.init() has run. It
    // must be resolved BEFORE identify: a context missing the bucketBy
    // attribute hashes to bucket 0.0, so every such visitor is served the
    // FIRST variation of the rollout rather than a random one. The dedupe ref
    // above is already committed synchronously, so awaiting here cannot
    // double-identify — but that also means an exception escaping this IIFE
    // would strand the effect, which can never retry for this context.
    // isLDidentified would stay false for the tab's life and the route-level
    // UI gated on it (packet, project, pricing) would never render. So the
    // read is guarded: identify with a null device id rather than not at all.
    // getDeviceId awaits initMixpanel, which awaits a Clerk token mint, so it
    // can reject on any Clerk or Mixpanel failure — including an ad blocker,
    // the same population the identify failure branch below exists for.
    (async () => {
      // Settle geo before building the context for the same reason mpDeviceId
      // is resolved first: an attribute that arrives after identify is invisible
      // to targeting for the rest of the session.
      await geoReady;
      let mpDeviceId = null;
      try {
        mpDeviceId = await Mixpanel.getDeviceId();
      } catch (err) {
        console.warn('Mixpanel device id unavailable; identifying without it', err);
      }
      if (isSignedIn) {
        const ldUserContext = {
          kind: 'user',
          key: user.id,
          email: user.primaryEmailAddress?.emailAddress,
          name: user.fullName,
          sessionId: diffitSessionId,
          mpDeviceId,
          // Spread, not two literal reads: an unresolved value must leave the
          // attribute ABSENT rather than set it to the 'UNKNOWN' sentinel, which
          // would satisfy `region exists` and every negative region clause.
          ...getResolvedGeo(),
          ...userAttributes,
          createdAt: new Date(diffitUser.created_at), // LD wants Date object
        };
        // Phone home to LaunchDarkly. On success we flip both flags true —
        // login tracking unblocks AND flag super-props can be registered with
        // the new context's values. On failure we flip only isLDidentified so
        // login tracking still fires (ad-blocked users still get identify +
        // 'Successful Login'), but isLDIdentifySuccess stays false so the
        // super-prop register is skipped — otherwise we'd stamp the previous
        // context's flag values (LD keeps the last successful cache on
        // failure) onto the new session's events.
        ldClient?.identify(ldUserContext).then(
          () => {
            setIsLDIdentifySuccess(true);
            setIsLDidentified(true);
          },
          (err) => {
            console.warn('LD identify failed; proceeding without flag attribution', err);
            setIsLDidentified(true);
          }
        );
      } else {
        ldClient
          ?.identify({
            kind: 'user',
            anonymous: true,
            sessionId: diffitSessionId,
            mpDeviceId,
            ...getResolvedGeo(),
          })
          .then(
            () => {
              setIsLDIdentifySuccess(true);
              setIsLDidentified(true);
            },
            (err) => {
              console.warn('LD identify failed; proceeding without flag attribution', err);
              setIsLDidentified(true);
            }
          );
      }
    })();
  }, [isSignedIn, ldClient, isLoaded, diffitUser, user, selectedOrg]);

  // Fetch the list of mixpanel-tagged flag names at mount. The LD JS SDK
  // doesn't expose flag tags, so the backend tells us which flags to stamp.
  // Retry on empty response — a transient backend failure at mount time
  // would otherwise leave the client with no flag attribution for the
  // whole session. Bounded retry count + exponential backoff so a
  // permanently-broken endpoint (404, auth issue) can't generate console
  // errors and network requests for the lifetime of the tab.
  useEffect(() => {
    let cancelled = false;
    let timer;
    let retryCount = 0;
    const MAX_RETRIES = 5;
    const BASE_DELAY_MS = 30000;
    const attempt = async () => {
      // First attempt reuses the request main.jsx already put in flight at
      // bootstrap; retries (empty response) issue a fresh one.
      const flags = await primeMixpanelTaggedFlags();
      if (cancelled) return;
      if (flags && flags.length > 0) {
        setTaggedFlagNames(flags);
      } else if (retryCount < MAX_RETRIES) {
        retryCount += 1;
        timer = setTimeout(attempt, BASE_DELAY_MS * retryCount);
      }
    };
    attempt();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Keep Mixpanel super-properties + profile in sync with the current LD
  // flag values. The client is the only source of truth for flag_* on
  // events — the backend does not stamp them. Reads values via
  // ldClient.allFlags() (preserves original kebab-case flag keys —
  // useFlags() auto-camelCases keys and can't be looked up by the
  // kebab-case names from the backend's mixpanel-tagged list). `flags`
  // is still a dep so the callback identity changes (and useLayoutEffect
  // re-fires) on any flag-value change. Skip undefined values so a
  // transient identify window doesn't wipe a previously-set super-prop.
  const registerFlagsToMixpanel = useCallback(() => {
    // Gate on success (not just "resolved") — if LD's identify rejected,
    // allFlags() returns the prior context's cached values, which would
    // mis-attribute the new session's events.
    if (!isLDIdentifySuccess || !taggedFlagNames || !ldClient) return;
    const all = ldClient.allFlags() || {};
    const register = {};
    const unregister = [];
    for (const name of taggedFlagNames) {
      if (all[name] !== undefined) {
        register[`flag_${name}`] = all[name];
      } else {
        // Actively unregister flags missing from the current context.
        // mixpanel.register merges, so without this a flag that was true
        // for a prior context would linger as a super-prop forever.
        unregister.push(`flag_${name}`);
      }
    }
    // Atomic AND synchronous — Mixpanel.replaceSuperProps has no suspension
    // point, so there is no window in which a concurrent Mixpanel.track can
    // observe half-updated super-props.
    Mixpanel.replaceSuperProps({ register, unregister });
    // Flag state rides only on events (as super-props). We intentionally do
    // NOT write it to the Mixpanel profile — profile props capture current
    // state, not state-at-event-time, which would cause cohorts defined on
    // profile props to drift from cohorts defined on event props.
  }, [isLDIdentifySuccess, flags, taggedFlagNames, ldClient]);

  // Ordering guarantee: useLayoutEffect runs synchronously during the commit
  // phase, before any component's useEffect starts, and this callback is now
  // fully synchronous end to end (Mixpanel init and super-prop registration
  // both are). So the flag_* super-props are on the SDK before the first
  // 'Page Viewed' track fires from the effect below. This used to depend on
  // both sides awaiting the same memoized init promise and the microtask queue
  // preserving order — reliable in practice, but not a guarantee.
  useLayoutEffect(() => {
    registerFlagsToMixpanel();
  }, [registerFlagsToMixpanel]);

  // Stamp basic user info onto every event as super-props, AS OF EVENT TIME.
  // These mirror values also sent via people.set in setUpDiffitUser, but
  // profile props capture only current state — the same reason the flag
  // super-props above ride on events instead of the profile. Re-runs on any
  // diffitUser change (login, initial load, refreshDiffitUser after a profile
  // edit) so events always carry the values current when they fired.
  const registerUserPropsToMixpanel = useCallback(async () => {
    // Wait until Clerk auth resolves so we never stamp a stale/unknown value.
    if (!isLoaded) return;
    // Anonymous: clear any lingering user super-props (mixpanel.register
    // merges) so anonymous events are cleanly queryable and never carry a
    // prior session's identity.
    if (!isSignedIn) {
      await Mixpanel.replaceSuperProps({
        unregister: USER_SUPER_PROP_KEYS,
      });
      return;
    }
    // Signed in, but the Diffit user record hasn't loaded yet (initial load,
    // or briefly in-flight). Defer the subscription/profile detail props until
    // diffitUser resolves. We don't clear the detail props here: sign-out
    // already reset()s them, so nothing stale can linger.
    if (!diffitUser) {
      return;
    }
    // The super-prop keys deliberately mirror the people.set profile keys in
    // setUpDiffitUser (is_premium_user / is_free_trial_user, etc.) so analysts
    // can compare the event view and the profile view side-by-side on matching
    // names — the divergence is only that these ride on events (event-time)
    // while people.set writes the profile (current state).
    const register = {
      subscription_status: getSubscriptionStatus(diffitUser),
      is_premium_user: diffitUser.is_premium,
      is_free_trial_user: diffitUser.is_free_trial,
      is_active_individual_subscription: diffitUser.is_active_individual_subscription,
      is_in_premium_org: diffitUser.is_in_premium_org,
      is_in_premium_domain: diffitUser.is_in_premium_domain,
      roles: diffitUser.roles?.map((r) => r.role) ?? [],
      grade_levels: diffitUser.grade_levels?.map((g) => g.grade_level) ?? [],
      subjects: diffitUser.subjects?.map((s) => s.subject) ?? [],
      user_segments: diffitUser.user_segments?.map((seg) => seg.name) ?? [],
      is_ambassador: diffitUser.is_ambassador,
      is_certified_educator: diffitUser.is_certified_educator,
      is_star_teacher: diffitUser.is_star_teacher,
      is_diffit_admin: diffitUser.is_diffit_admin,
      org_ids: diffitUser.org_user_infos?.map((org) => org.org_id) ?? [],
    };
    await Mixpanel.replaceSuperProps({ register });
  }, [isLoaded, isSignedIn, diffitUser]);

  useLayoutEffect(() => {
    registerUserPropsToMixpanel();
  }, [registerUserPropsToMixpanel]);

  // Emit a 'Flag State Identified' event once per successful LD identify
  // (per context). Provides a queryable record in Mixpanel of what flag
  // values the client evaluated for each user — used for cross-stack
  // consistency verification and to build dashboards on flag_* distributions
  // without having to rely on the super-props stamped on arbitrary events.
  //
  // It also doubles as the funnel entry for experiments on anonymous visitors.
  // The first 'Page Viewed' of a first-ever visit fires on mount, before
  // LaunchDarkly's first evaluation can return, so it carries no flag value and
  // no amount of chain-collapsing changes that (gating 'Page Viewed' on flag
  // readiness would drop bounces and bias the sample worse). This event, by
  // contrast, cannot exist without a resolved flag set, so it is 100% attributed
  // by construction. `path` and `is_first_context` are included so an
  // experiment's eligible population stays expressible as a filter on existing
  // properties — e.g. `isLoggedIn = false AND path = '/' AND is_first_context
  // = true` — which is the whole reason we don't need bespoke per-experiment
  // impression events.
  //
  // `is_first_context` is load-bearing, not decorative. This event re-emits on
  // every context transition (by design — see the identify effect), and sign-out
  // is a context transition that reverts to anonymous. So the re-emit after a
  // sign-out carries `isLoggedIn: false`, and on the homepage it would satisfy
  // an `isLoggedIn = false AND path = '/'` filter and enter a churned user into
  // the funnel as a fresh anonymous visitor. That can't be cleaned up
  // downstream: signOut() calls Mixpanel.reset(), which rotates the device id,
  // so the re-emit arrives under a brand-new identity that is indistinguishable
  // from a genuine first visit by anything except this property.
  //
  // Read from location.pathname at emit time rather than added to the dep array
  // on purpose: this fires once per LD context, not once per navigation.
  useEffect(() => {
    if (!isLDIdentifySuccess || !taggedFlagNames || !ldClient) return;
    if (flagStateEmittedRef.current) return;
    flagStateEmittedRef.current = true;
    const isFirstContext = !flagStateEverEmittedRef.current;
    flagStateEverEmittedRef.current = true;
    const all = ldClient.allFlags() || {};
    const flagValues = {};
    for (const name of taggedFlagNames) {
      flagValues[`flag_${name}`] = all[name] ?? null;
    }
    Mixpanel.track('Flag State Identified', {
      ...flagValues,
      source: 'client',
      path: location.pathname,
      is_first_context: isFirstContext,
    });
  }, [isLDIdentifySuccess, taggedFlagNames, ldClient]);

  // Fire the post-identify Mixpanel calls only after LD identify has completed
  // AND the flag super-props are registered, so people.set / 'Successful
  // Login' carry the correct flag_* values. Runs for BOTH fresh logins
  // (Path A) and already-signed-in loads (Path B); pending.trackLoginEvent
  // distinguishes them. The pending response lives on a ref, so the
  // isLDidentified transition to `true` after the ref is populated is what
  // wakes this effect.
  useEffect(() => {
    const pending = pendingLoginResponseRef.current;
    if (!pending || !isLDidentified || !ldClient || !taggedFlagNames || loginTrackedRef.current) {
      return;
    }
    const login = pending.user;
    // Set the de-dupe ref before awaiting so a re-render mid-chain (e.g.
    // from a flag-value update) doesn't kick off a second IIFE. On error
    // we reset the ref so a subsequent render can retry.
    loginTrackedRef.current = true;
    (async () => {
      try {
        // Mixpanel.identify(login.id) already fired in the load effect so
        // setUpDiffitUser's people.set calls landed on the user profile.
        // Here we only need to register flag super-props before the
        // 'Successful Login' track, and to set the user-fields profile
        // payload.
        await registerFlagsToMixpanel();
        await Mixpanel.people.set({
          user_id: login.id,
          clerk_id: login.clerk_id,
          name: login.name,
          given_name: login.given_name,
          family_name: login.family_name,
          email: login.email,
          user_login_type: getUserLoginType(user),
        });
        // Only emit the login event on an actual fresh login (Path A). For
        // already-signed-in loads we still set the profile fields above, but
        // firing 'Successful Login' on every refresh would inflate the metric.
        if (pending.trackLoginEvent) {
          // `?? null` (not `!!`): a missing/stale backend field lands in its
          // own Mixpanel breakdown bucket rather than quietly inflating the
          // "returning" (false) count during the deploy window or on drift.
          await Mixpanel.track('Successful Login', {
            is_new_signup: login.was_created ?? null,
          });
          // Fires whenever 'Successful Login' does, including on a sign-up: a new
          // account is also a login, and Mixpanel/GA already model it that way.
          // Keeping Meta consistent means the three can be cross-checked.
          // trackLoginEvent gates this to a real fresh login, so a page refresh
          // while signed in doesn't inflate it.
          // Unlike the Mixpanel call above, a missing was_created doesn't get its
          // own bucket here — the wrapper strips null, so the param is simply
          // absent on the events where the backend didn't report it.
          trackMetaCustomEvent('LoggedIn', { is_new_signup: login.was_created ?? null });
          // Same moment for ChatGPT Ads, on the same trackLoginEvent gate.
          // No is_new_signup: OpenAI's custom shape takes no arbitrary metadata
          // (only amount/currency/contents/plan_id), so the new-vs-returning
          // split lives in Mixpanel and Meta, and registration_completed below
          // is the signal that isolates sign-ups here.
          trackChatGPTCustomEvent('logged_in');
          // First-time sign-up: durably stamp the signup date on the profile so
          // "signed up in period X" cohorts don't depend on event retention.
          // set_once is idempotent, so the ?success-reload re-fire (which sees
          // was_created:false) can't overwrite it and the true event firing
          // twice can't either.
          if (login.was_created) {
            // Pass a Date (not the RFC-2822 string the API serializes) so the
            // Mixpanel SDK stores a real date property that date-range cohorts
            // can filter on.
            await Mixpanel.people.set_once({ signed_up_at: new Date(login.created_at) });
            // GA activation funnel: was_created means the account row was just
            // inserted — a completed sign-up, not a returning login (and the
            // ?success-reload re-fire sees was_created:false, so no double-count).
            trackGAEvent('sign_up_completed', { method: getUserLoginType(user) });
            // Meta's conversion event for the same moment, so Facebook ad sets
            // can optimize on completed sign-ups. Gated on the same was_created
            // check for the same reason.
            // Not `status`: Meta types it boolean, so a login method there is an
            // invalid parameter value. A named custom parameter keeps it legible
            // in Events Manager without overloading a standard field.
            // The method is a fixed set ('google', 'email', ...), not user text.
            trackMetaEvent('CompleteRegistration', {
              registration_method: getUserLoginType(user),
            });
            // OpenAI's standard conversion for the same moment, so ChatGPT Ads
            // campaigns can optimize on completed sign-ups. Same was_created
            // gate, so the ?success-reload re-fire can't double-count it.
            // registration_method has no home in the customer_action shape.
            trackChatGPTEvent('registration_completed');
          }
        }
        pendingLoginResponseRef.current = null;
      } catch (err) {
        console.warn('Login tracking failed; will retry on next render', err);
        loginTrackedRef.current = false;
      }
    })();
  }, [isLDidentified, ldClient, taggedFlagNames, registerFlagsToMixpanel, user]);

  useEffect(() => {
    Mixpanel.track('Page Viewed', { path: location.pathname });

    // Sole source of Meta PageView. The base snippet in index.html deliberately
    // inits without tracking, because a load-time-only PageView would miss every
    // client-side route change in this SPA. This effect also runs on mount, so
    // the first page of a session is covered here too.
    trackMetaEvent('PageView');

    // Sole source of ChatGPT page_viewed, for the same reason: the SDK does
    // not auto-track page views, and the init in index.html deliberately
    // doesn't send one because a load-time-only call would miss every
    // client-side route change. This effect also runs on mount.
    trackChatGPTEvent('page_viewed');

    // Track page view in Customer.io as well
    cioPage(location.pathname, {
      url: window.location.href,
      path: location.pathname,
      search: location.search,
      hash: location.hash,
      referrer: document.referrer,
    });
  }, [location.pathname]);

  return (
    <ErrorProvider>
      <TutorialProvider>
        <DiffitUserContext.Provider
          value={{
            // The masked user, so every consumer of this context agrees about
            // who is looking. setDiffitUser still writes the real one — the mask
            // is a view, not a change of identity.
            diffitUser: viewedDiffitUser,
            setDiffitUser,
            isViewingAsTeacher,
            // Unmasked, for staff TOOLING only. Anything describing what a
            // teacher sees must read diffitUser.is_diffit_admin above; this is
            // for the controls that operate the mask, which cannot key on the
            // value they are hiding or they switch themselves off.
            isRealDiffitAdmin: !!diffitUser?.is_diffit_admin,
            selectedOrg,
            setSelectedOrg,
            isDiffitBot,
            isPremiumUser,
            isFreeTrialUser: isFreeTrialUser(user, diffitUser),
            daysLeftInTrial: daysLeftInTrial(user, diffitUser),
            hasActiveSubscription: hasActiveSubscription(diffitUser),
            userLoginType: getUserLoginType(user),
            canHaveFreeTrialExtension,
            isSignedIn,
            refreshDiffitUser,
            isLoadingDiffitUser,
            finishedLoadingOnboardingState,
            isLDidentified,
            setShowEditProfileModal,
            showPendingNotification,
            signOut,
          }}>
          <SidebarVisibilityProvider>
            <PremiumOfferProvider>
              <SignInModalProvider>
                <div
                  className='flex h-screen'
                  style={{
                    '--top-nav-height': isMobile ? NAVBAR_MOBILE_HEIGHT : '0px',
                    marginTop: isMobile ? NAVBAR_MOBILE_HEIGHT : 0,
                  }}>
                  <AppNavigation />
                  <main className='flex-1 flex flex-col min-w-0 h-screen'>
                    <>
                      <EmergencyFrontendBanner classes='sticky top-0 z-[99999]' />
                      <CustomerIOPlaceholder id='customerio-banner' />
                    </>
                    <Outlet />
                  </main>
                </div>
                <ForcedSignupPrompt />
                <PremiumOfferDeepLink />
                <ReferralWelcomePrompt />
                <SignInSuccessModal open={showNewUserModal} setOpen={setShowNewUserModal} />
                {diffitUser && showOnboardingWizardBts2026 && !isOnPricingPage && (
                  <OnboardingWizardBTS2026
                    showWizard={showOnboardingWizardBts2026}
                    setShowWizard={() => {
                      refreshDiffitUser();
                      setShowOnboardingWizardBts2026(false);
                    }}
                  />
                )}
                {showBadgeAwardedModal && !isOnPricingPage && (
                  <BadgeAwardedModal
                    badges={badgesToAnnounce}
                    onClose={() => setDismissedBadgeModal(true)}
                  />
                )}
                {/* Gated on the wizard being closed so a freshly signed-up referee — who sees
                  both — doesn't get two modals stacked on top of each other. */}
                {diffitUser && !showOnboardingWizardBts2026 && !isOnPricingPage && (
                  <RefereeRewardModal />
                )}
                {diffitUser && showEditProfileModal && (
                  <EditProfileModal
                    showModal={Boolean(showEditProfileModal)}
                    initialSection={
                      typeof showEditProfileModal === 'string' ? showEditProfileModal : null
                    }
                    setShowModal={() => {
                      refreshDiffitUser();
                      setShowEditProfileModal(false);
                    }}
                  />
                )}
                <ProjectQuotaModal />
              </SignInModalProvider>
            </PremiumOfferProvider>
          </SidebarVisibilityProvider>
        </DiffitUserContext.Provider>
      </TutorialProvider>
      <ErrorModal />
    </ErrorProvider>
  );
}
